import { Trash2 } from 'lucide-react'

import type { PackagingDraftItem } from '@/lib/packaging-draft'
import { Input } from '@/components/ui/input'
import { formatVND } from '@/lib/format'
import { rowKeyOf } from '@/lib/material-row-id'
import { m } from '@/lib/paraglide/messages'

interface Props {
    row: PackagingDraftItem
    /** true = vật tư nhân viên tự thêm, ngang hàng dòng sản phẩm (có hậu tố, không
     *  thụt lề). false = vật tư theo định mức, thụt lề dưới đúng sản phẩm của nó. */
    standalone?: boolean
    onQtyChange: (rowKey: string, value: string) => void
    onPriceChange: (rowKey: string, value: string) => void
    onDiscountChange: (rowKey: string, value: string) => void
    onRemove: (rowKey: string) => void
}

function fmtQty(n: number) {
    return n.toLocaleString('vi-VN', { maximumFractionDigits: 3 })
}

/**
 * Thẻ vật tư ở màn tạo đơn MOBILE — hiển thị trong danh sách hàng hóa thay cho
 * bảng "Vật tư bao bì" cũ. Mọi dòng đều sửa được SL + đơn giá + CK, hiện
 * tồn/thiếu và xoá được.
 *
 * Mọi callback nhận KHOÁ DÒNG (rowKeyOf) chứ không phải material_id: cùng một
 * vật tư có thể xuất hiện trên nhiều dòng.
 */
export function MobileMaterialCard({
    row,
    standalone = false,
    onQtyChange,
    onPriceChange,
    onDiscountChange,
    onRemove,
}: Props) {
    const rowKey = rowKeyOf(row)
    // CK chỉ có nghĩa khi có tiền để trừ — đơn giá 0 thì thành tiền luôn 0, ô
    // nhập chỉ gây hiểu nhầm.
    const priced = row.unit_price > 0
    const shortage = row.quantity > row.available
    const lineAmount =
        row.unit_price * row.quantity * (1 - (row.discount_percent ?? 0) / 100)

    // Một class nền duy nhất theo ưu tiên: thiếu tồn (vàng) > thêm tay (card) >
    // định mức (mờ). Nếu để bg-card và bg-amber-50 cùng xuất hiện, Tailwind không
    // đảm bảo cái nào thắng nên cảnh báo thiếu tồn có thể không đổi màu.
    const bg = shortage ? 'bg-amber-50' : standalone ? 'bg-card' : 'bg-muted/20'

    return (
        <div
            className={`rounded-md border p-2.5 ${
                standalone ? '' : 'ml-3 border-l-2 border-l-primary/30'
            } ${bg}`}
        >
            {/* Tên vật tư */}
            <div className="flex items-center gap-1 text-sm">
                {standalone ? null : (
                    <span className="shrink-0 text-muted-foreground">↳</span>
                )}
                <span className="min-w-0 flex-1 truncate font-medium">
                    {row.material_name}
                </span>
                {standalone ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                        {m.material_row_suffix()}
                    </span>
                ) : null}
            </div>

            {/* Tồn / thiếu */}
            <div className="mt-0.5 text-xs text-muted-foreground">
                {m.packaging_editor_col_available()}: {fmtQty(row.available)}
                {shortage ? (
                    <span className="ml-1 text-amber-700">
                        ({m.packaging_editor_badge_shortage()})
                    </span>
                ) : null}
            </div>

            {/* SL + đơn giá + CK + xoá */}
            <div className="mt-1.5 flex items-end gap-2">
                <div className="flex w-16 shrink-0 flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">
                        {m.order_quantity()}
                    </span>
                    <Input
                        type="number"
                        min={0}
                        step="any"
                        inputMode="decimal"
                        value={row.quantity}
                        onChange={(e) => onQtyChange(rowKey, e.target.value)}
                        className="min-h-10 text-base"
                        aria-label={`${row.material_name} SL`}
                    />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">
                        {m.order_unit_price()}
                    </span>
                    <Input
                        type="number"
                        min={0}
                        step="any"
                        inputMode="decimal"
                        value={row.unit_price}
                        onChange={(e) => onPriceChange(rowKey, e.target.value)}
                        className="min-h-10 text-base"
                        aria-label={`${row.material_name} ${m.order_unit_price()}`}
                    />
                </div>
                {priced ? (
                    <div className="flex w-14 shrink-0 flex-col gap-0.5">
                        <span className="text-xs text-muted-foreground">
                            {m.order_discount_short()}
                        </span>
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            step="any"
                            inputMode="decimal"
                            value={row.discount_percent}
                            onChange={(e) =>
                                onDiscountChange(rowKey, e.target.value)
                            }
                            className="min-h-10 text-base"
                            aria-label={`${row.material_name} CK %`}
                        />
                    </div>
                ) : null}
                <button
                    type="button"
                    onClick={() => onRemove(rowKey)}
                    className="mb-1 flex min-h-10 min-w-10 shrink-0 items-center justify-center p-2 text-destructive"
                    aria-label={`${m.common_aria_delete()} ${row.material_name}`}
                >
                    <Trash2 className="size-4" />
                </button>
            </div>

            {/* Thành tiền */}
            <div className="mt-1 text-right text-sm font-medium text-muted-foreground">
                {formatVND(lineAmount)}
            </div>
        </div>
    )
}
